import {ActionButton, ActionButtonProps} from './ActionButton'
import {BreakStyle, defineStylesWithBreaks, useThemedStylesWithBreaks} from './styles/style'
import React, {CSSProperties, ReactNode, useEffect, useMemo, useRef} from 'react'
import {horizontalButtonContainer, modalContainerStyle, modalContainerStyleMobile} from './shared'

import {Portal} from './Portal'
import {SVG} from '.'

export interface CommonModalProps {
  children: ReactNode
  onClose: () => void
  title?: string
  buttons?: ActionButtonProps[]
  errors?: string[]
  closeStyle?: 'button' | 'click-away' | 'button-or-click' | 'none'
  backgroundStyle?: CSSProperties
  contentContainerStyle?: CSSProperties
  boxStyle?: CSSProperties
  modalElementsStyle?: CSSProperties
  headerStyle?: CSSProperties
  buttonContainerStyle?: CSSProperties
  errorStyle?: CSSProperties
  fadeBackground?: boolean
  closeOnEscape?: boolean
}

export const modalContentDefaultHeightPercent = 0.7
export const modalContentConstrainedHeightPercent = 0.95

export const modalContentPaddingMedium = '36px'
export const modalContentPaddingLarge = '48px'

const modalBackgroundId = 'modalBackground'

let nextModalId = 0
const modalStack: number[] = []

export function CommonModal({
  title,
  children,
  onClose,
  errors,
  buttons,
  backgroundStyle,
  boxStyle,
  modalElementsStyle,
  contentContainerStyle,
  headerStyle,
  buttonContainerStyle,
  errorStyle,
  closeStyle = 'button-or-click',
  fadeBackground = true,
  closeOnEscape = false
}: CommonModalProps) {
  const s = useThemedStylesWithBreaks(styles)

  const closeOnClickAway = closeStyle === 'click-away' || closeStyle === 'button-or-click'
  const hasCloseButton = closeStyle === 'button' || closeStyle === 'button-or-click'
  const hasHeader = hasCloseButton || !!title

  const localBoxStyle = useMemo(() => {
    return {
      ...s.modalOutline,
      ...boxStyle
    }
  }, [s, boxStyle])

  const localModalElementsStyle = useMemo(() => {
    return {
      ...s.modalElements,
      ...modalElementsStyle
    }
  }, [s, modalElementsStyle])

  const localContainerStyle = useMemo(() => {
    return {
      ...s.contentContainer,
      ...contentContainerStyle
    }
  }, [s, contentContainerStyle])

  const localBackgroundStyle = useMemo(() => {
    const baseStyle = fadeBackground ? s.fixedCanvasFaded : s.fixedCanvasUnfaded

    return {...baseStyle, ...backgroundStyle}
  }, [s.fixedCanvasFaded, s.fixedCanvasUnfaded, fadeBackground, backgroundStyle])

  useEffect(() => {
    function cancelMove(e: TouchEvent) {
      if (e.target && (e.target as any).id === modalBackgroundId) {
        e.preventDefault()
        e.stopImmediatePropagation()
      }
    }

    document.addEventListener('touchmove', cancelMove, {passive: false})

    return () => {
      document.removeEventListener('touchmove', cancelMove)
    }
  }, [])

  const modalIdRef = useRef<number | null>(null)
  useEffect(() => {
    const id = nextModalId++
    modalIdRef.current = id
    modalStack.push(id)
    return () => {
      const index = modalStack.indexOf(id)
      if (index !== -1) modalStack.splice(index, 1)
    }
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape' || e.isComposing || e.defaultPrevented) return
      if (modalStack[modalStack.length - 1] !== modalIdRef.current) return
      if (closeOnEscape && closeStyle !== 'none') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, closeOnEscape, closeStyle])

  return (
    <Portal>
      <div
        id={modalBackgroundId}
        style={localBackgroundStyle}
        onClick={(e) => {
          closeOnClickAway && onClose()
          e.stopPropagation()
        }}
      >
        <div style={localBoxStyle} onClick={(e) => e.stopPropagation()}>
          <div style={localModalElementsStyle}>
            {hasHeader ? (
              <div style={{...s.header, ...(headerStyle ?? {})}}>
                {title ? <div style={s.modalTitle}>{title}</div> : null}
                {hasCloseButton ? (
                  <ActionButton
                    onClick={onClose}
                    style={s.closeButton}
                    appearance="plain"
                    size="small"
                    iconCreator={(color) => <SVG.CloseSmall color={color} />}
                  />
                ) : null}
              </div>
            ) : null}
            <div style={localContainerStyle}>{children}</div>
            {errors ? (
              <div style={{...s.error, ...(errorStyle ?? {})}}>
                {errors.map((e, i) => (
                  <div key={`error-${i}`}>{e}</div>
                ))}
              </div>
            ) : null}
            {buttons ? (
              <div style={{...s.bottomButtonContainer, ...(buttonContainerStyle ?? {})}}>
                {buttons.map((props: ActionButtonProps, index) => (
                  <ActionButton key={index} {...props} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Portal>
  )
}

const styles = defineStylesWithBreaks((theme, font) => {
  const fixedCanvasFaded: BreakStyle = {
    position: 'fixed',
    display: 'flex',
    left: 0,
    top: 0,
    height: '100vh',
    width: '100vw',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    backgroundColor: `rgba(0, 0, 0, 0.75)`,
    color: theme.core.f1
  }

  return {
    fixedCanvasFaded,

    fixedCanvasUnfaded: {
      ...fixedCanvasFaded,
      backgroundColor: 'inherit'
    },

    modalCanvas: {
      position: 'relative'
    },

    modalOutline: {
      ...modalContainerStyle(theme),
      ...modalContainerStyleMobile
    },

    modalElements: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    },

    header: {
      padding: '32px 32px 0',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
      ...font.bodyBold
    },

    closeButton: {
      position: 'absolute',
      right: '24px'
    },

    modalTitle: {
      textAlign: 'center',
      overflowWrap: 'break-word',
      maxWidth: '100%'
    },

    error: {
      color: theme.alert.base,
      marginTop: '20px',
      textAlign: 'center'
    },

    contentContainer: {
      width: '100%',
      maxHeight: `${100 * modalContentDefaultHeightPercent}vh`,
      padding: `${modalContentPaddingMedium} ${modalContentPaddingLarge}`,
      overflowY: 'auto',

      mobile: {
        padding: modalContentPaddingMedium
      }
    },

    bottomButtonContainer: {
      ...horizontalButtonContainer,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 0 36px'
    }
  }
})
